import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const PrivacyPage = () => {
  const lastUpdated = 'January 1, 2026';

  return (
    <>
      <Helmet>
        <title>Privacy Policy - CalcBuildAI</title>
        <meta name="description" content="Privacy Policy for CalcBuildAI. Learn how we collect, use, and protect your data." />
      </Helmet>

      <div className="min-h-screen bg-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: {lastUpdated}</p>

          <div className="prose prose-gray max-w-none">
            <p className="lead">
              At CalcBuildAI ("we", "our", or "us"), we take your privacy seriously. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our website and services.
            </p>

            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Information You Provide</h3>
            <p>We collect information you voluntarily provide, including:</p>
            <ul>
              <li><strong>Account Information:</strong> Email address and password when you create an account</li>
              <li><strong>Profile Information:</strong> Display name and preferences you choose to add</li>
              <li><strong>Calculation Data:</strong> Project descriptions and calculations you submit</li>
              <li><strong>Communications:</strong> Messages you send us through contact forms</li>
              <li><strong>Payment Information:</strong> Billing details processed through Stripe (we don't store credit card numbers)</li>
            </ul>

            <h3>1.2 Information Collected Automatically</h3>
            <p>When you use our service, we automatically collect:</p>
            <ul>
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, calculation history</li>
              <li><strong>Log Data:</strong> IP address, access times, referring URLs</li>
              <li><strong>Cookies:</strong> See our <Link to="/cookies">Cookie Policy</Link> for details</li>
            </ul>

            <h2>2. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul>
              <li>Provide, maintain, and improve our services</li>
              <li>Process your calculations and save your history</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send service-related communications</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Analyze usage patterns to improve user experience</li>
              <li>Detect, prevent, and address technical issues and fraud</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2>3. Information Sharing</h2>
            <p>We do not sell your personal information. We may share data with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third parties that help us operate (hosting, payment processing, analytics)</li>
              <li><strong>Stripe:</strong> For payment processing (see Stripe's Privacy Policy)</li>
              <li><strong>Supabase:</strong> For authentication and data storage</li>
              <li><strong>Anthropic:</strong> For AI-powered calculations (queries are processed but not stored by Anthropic)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            </ul>

            <h2>4. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed 
              to provide services. Calculation history is kept until you delete it or your account. 
              You can request deletion of your data at any time through your account settings.
            </p>

            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your data, 
              including encryption in transit (HTTPS) and at rest, secure authentication, and 
              regular security assessments. However, no method of transmission over the internet 
              is 100% secure.
            </p>

            <h2>6. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul>
              <li>Access and receive a copy of your personal data</li>
              <li>Correct inaccurate personal data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@calcbuildai.com">privacy@calcbuildai.com</a> or use the 
              options in your account settings.
            </p>

            <h2>7. California Privacy Rights (CCPA)</h2>
            <p>
              California residents have additional rights under the CCPA, including the right to 
              know what personal information we collect, request deletion, and opt-out of the 
              sale of personal information (we do not sell personal information).
            </p>

            <h2>8. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place for such transfers in compliance with 
              applicable data protection laws.
            </p>

            <h2>9. Children's Privacy</h2>
            <p>
              Our service is not directed to children under 13 (or 16 in the EEA). We do not 
              knowingly collect personal information from children. If you believe a child has 
              provided us with personal information, please contact us.
            </p>

            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any 
              material changes by posting the new policy on this page and updating the "Last 
              updated" date.
            </p>

            <h2>11. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <ul>
              <li>Email: <a href="mailto:support@calcbuildai.com">support@calcbuildai.com</a></li>
              <li>Contact Form: <Link to="/contact">Contact Page</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPage;
